import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Check, AlertCircle, ChevronRight } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import StudioWorkspace from './StudioWorkspace';
import StudioBoard from './StudioBoard';
import CrewStrip from './CrewStrip';
import DirectorChair from './DirectorChair';
import { STREAMS, type StreamDef } from './StreamPicker';
import CopyrightPanel from './CopyrightPanel';
import CastBible from './CastBible';
import MediaAliasPicker from './MediaAliasPicker';
import PreviewStrip from './PreviewStrip';
import ShotScriptView from './ShotScriptView';
import InfoHint from './InfoHint';
import TopUpModal from '../wallet/TopUpModal';
import { TYPE } from '../../constants/typeScale';
import StoryStage from './StoryStage';
import ProjectStage, { projectFilled } from './ProjectStage';
import PublishToStore from './PublishToStore';
import applySuggestion from './applySuggestion';
import CaptionedVideo from './CaptionedVideo';
import ScriptPreview from './ScriptPreview';
import StageStepper from './StageStepper';
import TrialNotice from './TrialNotice';
import { stageStatuses, resumeStage, isWrittenShot, type StageKey } from './studioStages';
import { describePreviewOutcome } from './previewOutcome';
import type { MediaAlias, MusicSpan, FormatIssue, CaptionCue } from '../../services/studioService';
import { listSeriesCast, type Character } from '../../services/characterService';
import {
  getStudioToggles,
  getStudioSpecialists,
  getMediaAliases,
  assistShot as assistShotApi,
  getStudioGenres,
  type StudioGenre,
  type ShotDraft,
  draftBeatSheet,
  createStoryboard,
  updateStoryboard,
  renderStoryboard,
  remakeShot as remakeShotApi,
  restoreTake as restoreTakeApi,
  previewStoryboardFrames,
  getStoryboard,
  getCrewCatalog,
  recruitCrewAgent,
  dismissCrewAgent,
  type ToggleGroup,
  type StoryboardShot,
  type StoryboardStatus,
  type StoryboardSound,
  type StudioSpecialist,
  type Series,
  type SeriesBible,
  REQUIRED_BIBLE_FIELDS,
  listSeries,
  updateSeries,
  getMyStore,
} from '../../services/studioService';
import { useFinishedFilm } from './useFinishedFilm';
import { useOptionalToast } from '../../contexts/NotificationContext';
import { addSuggestedAgent, type ConversationSummary } from '../../services/conversationService';

interface Props {
  open: boolean;
  onClose?: () => void;
  /** Keep the left project/media explorer visible beside the Studio (workstation layout). */
  leftRail?: 'open' | 'rail';
  /** The left pane's LIVE width, so a dragged border moves the canvas with it and never overlaps. */
  leftWidth?: string;
  /** Seed idea from the chat launcher ("make a 30s coffee ad"). */
  initialIdea?: string;
  /** The chat this Studio session launched from (optional). */
  conversationId?: string | null;
  /** The current user's uid (for owner-scoped reference-frame uploads). */
  uid: string;
  /** Catalog profile ids of the agents already in the chat (so the crew strip recommends only the missing ones). */
  memberProfileIds?: string[];
  /** Launched from the dashboard with a pre-chosen work stream → skip the picker, seed that stream's defaults. */
  initialStream?: StreamDef | null;
  /** Launched from the dashboard to reopen an existing production → load this storyboard on open. */
  openStoryboardId?: string | null;
  /** Told when the Studio expands/shrinks, so the chat behind it can narrow into the co-pilot rail. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Whether the chat pane is currently slid open (host-owned, so the chat column can animate with it). The
   *  control that flips it lives ON that pane now, not in our header (owner 2026-08-21). */
  chatRailOpen?: boolean;
  /** The right pane's LIVE width, same deal as `leftWidth`. */
  chatWidth?: string;
  /** False while either pane's border is being dragged — the canvas tracks the pointer instead of easing. */
  smooth?: boolean;
  /**
   * Is there a right pane mounted beside us at all? On the workstation the pane is a two-level surface
   * (chat contacts → a chat), so it exists even when this Studio launched from no chat — the old
   * `conversationId` proxy would leave that pane with no room reserved. Defaults to the old behaviour.
   */
  chatRailAvailable?: boolean;
  /**
   * The production's crew chat, whenever we learn it: on load (the storyboard carries the link) and on the
   * recruit that FOUNDS it (which also hands back the fresh conversation, since the host's list predates it).
   */
  onCrewChatResolved?: (info: { conversationId: string | null; conversation?: ConversationSummary }) => void;
  /** The stage to open on, from the URL (/dashboard/{slug}/{stage}). */
  initialStage?: StageKey | null;
  /** Told whenever the creator moves stage, so the host can put it in the URL. */
  onStageChange?: (stage: StageKey) => void;
  /** The workstation's home (/dashboard). When given, the breadcrumb starts with "Your series". */
  onHome?: () => void;
  /** True only when the ADDRESS names an unsaved episode (/dashboard/new/...): the one case where opening the
   *  series page may create the episode and its series. Anything else is an episode still loading. */
  unsavedEpisode?: boolean;
  /** Told once when a brand-new episode is first saved, with its /dashboard handle (slug, else id), so the
   *  host can move the address off /dashboard/new. */
  onEpisodeCreated?: (handle: string) => void;
}

// W13 format-fit: the proven micro-drama episode runs 60–120s (US+China research 2026-08-11 — ReelShort/
// DramaBox/duanju all converged on the 1–2 minute band). 90s was previously unreachable and 120s was the
// commercial standard we couldn't express; ads stay short, so the short end is kept for the hook streams.
/** Must match MAX_SHOTS in functions/src/api/handlers/studio.ts — the server SILENTLY truncates past it, so
 *  the UI has to stop a creator before they write a shot that would vanish on save. */
const MAX_SHOTS = 12;
/** How long a keystroke rests before the draft writes itself. A stage change skips it (see `pendingFlush`). */
const AUTOSAVE_DEBOUNCE_MS = 2500;
const emptyShot = (): StoryboardShot => ({ toggles: {} });

/**
 * StudioPanel — the Video Studio Story step: an idea + length, an editable list of plain-English shot cards (with
 * guided toggles + the AI Assist copilot), inside the right-pane workspace shell. Saves the storyboard as a draft;
 * the full render pipeline (per-shot generation + assembly) lands with the async render job.
 */
const StudioPanel: React.FC<Props> = ({ open, onClose, leftRail, leftWidth, initialIdea, conversationId, uid, memberProfileIds = [], initialStream = null, openStoryboardId = null, onExpandedChange, chatRailOpen, chatWidth, smooth, chatRailAvailable, onCrewChatResolved, initialStage = null, onStageChange, onHome, onEpisodeCreated, unsavedEpisode = false }) => {
  const [idea, setIdea] = useState(initialIdea || '');
  const [duration, setDuration] = useState(30);
  const [sound, setSound] = useState<StoryboardSound>({ kind: 'none', mood: null });
  const [musicSpans, setMusicSpans] = useState<MusicSpan[]>([]); // the audio-lane spans (advanced; empty = use the single bed)
  const [shots, setShots] = useState<StoryboardShot[]>([emptyShot()]);
  const [toggleGroups, setToggleGroups] = useState<ToggleGroup[]>([]);
  const [specialists, setSpecialists] = useState<StudioSpecialist[]>([]);
  /** Popular-plot templates for the Story plot library — the genre catalog the old chip row used to draw from. */
  const [genres, setGenres] = useState<StudioGenre[]>([]);
  const [aliases, setAliases] = useState<MediaAlias[]>([]);
  const [videoAlias, setVideoAlias] = useState<string>('');
  // How same-place shots connect — surfaced as Flowing / Distinct / Auto (never "chain"). null = auto default.
  const [continuityMode, setContinuityMode] = useState<'off' | 'chain' | 'auto'>('auto');
  const [cast, setCast] = useState<Character[]>([]);
  const [assistingIndex, setAssistingIndex] = useState<number | null>(null);
  const [storyboardId, setStoryboardId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<StoryboardStatus | null>(null);
  // True from the instant Generate is clicked through the persist+kickoff round-trip (before status flips to
  // 'generating') — so the button shows a spinner immediately instead of looking like the click did nothing.
  const [starting, setStarting] = useState(false);
  const [renderedCount, setRenderedCount] = useState(0);
  /** The finished cut's Storage path — server truth for "this episode has a film"; the URL is only for playback. */
  const [finalPath, setFinalPath] = useState<string | null>(null);
  const { url: finalUrl, failed: finalUrlFailed } = useFinishedFilm(finalPath);
  // Captions ride with the finished cut — the format is watched muted first, so they are how most of an
  // audience hears an episode at all.
  const [captions, setCaptions] = useState<CaptionCue[] | null>(null);
  /**
   * IS THIS EPISODE ON THE CREATOR'S STORE? (RF3-STORE). Held here rather than derived, because publishing is
   * an explicit act with an optimistic toggle — the control flips this the moment it is pressed and puts it
   * back if the server refuses.
   */
  const [published, setPublished] = useState(false);
  const { showSuccess, showError, showInfo, showConfirm } = useOptionalToast();
  // Studio is a creation CANVAS, not a chat widget — open it full-screen by default so the value prop (the
  // storyboard) is immediately usable with zero clicks. The Restore toggle re-docks it beside the chat for
  // anyone who wants to reference the conversation. (SteveJobs: don't make the user expand to reach the work.)
  /** The Studio is ALWAYS full screen (owner 2026-08-14) — there is no side-panel mode to be in. Kept as a
   *  const so the layout branches below still read honestly rather than being silently deleted. */
  const maximized = true;
  // Tell the host whether the chat behind us should narrow into the co-pilot rail. A CLOSED studio never
  // reserves anything, so the chat always gets its full width back on close.
  useEffect(() => { onExpandedChange?.(open && maximized); }, [open, maximized, onExpandedChange]);
  const [selectedShot, setSelectedShot] = useState(0);
  /**
   * THE SHOTS PAGE OPENS AS A SCRIPT (UX-3, owner chose design 1c, 2026-10-21): readable shots beside their pictures.
   * The board is behind Arrange. The choice is remembered per browser; storage can be blocked, so it is guarded.
   */
  const [shotsView, setShotsView] = useState<'script' | 'board'>(() => {
    try { return window.localStorage.getItem('wt_shots_view') === 'board' ? 'board' : 'script'; } catch { return 'script'; }
  });
  const chooseShotsView = useCallback((v: 'script' | 'board') => {
    setShotsView(v);
    try { window.localStorage.setItem('wt_shots_view', v); } catch { /* not remembered; still switches */ }
  }, []);
  /**
   * WHICH STAGE the creator is on. The Studio used to show every control for every phase at once in a 360px
   * rail — complete, and unusable: you could not tell what to do next, and changing anything meant hunting
   * through everything. `studioStages` already mapped the journey 1:1 to PRODUCT.md's stages and was fully
   * built and tested; it had simply never been mounted. This is that navigation, over the same document.
   */
  /**
   * Whether a DIRECTOR is actually hired, derived the same way DirectorChair derives it — is one of the
   * director-role agents among the chat's members? This was hardcoded `false` in `stageStatuses`, so the Crew
   * step could never report done and the stepper quietly lied about the creator's progress.
   */
  const [directorHired, setDirectorHired] = useState(false);
  /** Agents recruited onto the crew in THIS session — merged with the chat roster below, so a hire shows up
   *  even on a production opened from the dashboard (which has no chat mounted to read a roster from). */
  const [recruited, setRecruited] = useState<string[]>([]);
  /** Who is on this production's crew right now: the chat's roster plus anyone just recruited. */
  const crewIds = useMemo(() => Array.from(new Set([...memberProfileIds, ...recruited])), [memberProfileIds, recruited]);
  /** The writer bench — who can develop the script, each with a distinct style (RF3-STORY). */
  const [writers, setWriters] = useState<Array<{ agentId: string; name: string; flavor: string; hint: string }>>([]);
  /** The PROJECT lead's bench — writers AND directors, since a project's decisions are a showrunner's. */
  const [leads, setLeads] = useState<Array<{ agentId: string; name: string; flavor: string; hint: string }>>([]);
  useEffect(() => {
    let alive = true;
    getCrewCatalog()
      .then((c) => {
        if (!alive) return;
        const directors = new Set((c.roles || [])
          .filter((r) => r.role === 'director')
          .flatMap((r) => r.flavors.map((f) => f.agentId)));
        setDirectorHired(crewIds.some((id) => directors.has(id)));
        const bench = (c.roles || []).find((r) => r.role === 'writer')?.flavors || [];
        setWriters(bench.map((f) => ({ agentId: f.agentId, name: f.name, flavor: f.flavor, hint: f.hint })));
        /**
         * THE PROJECT'S BENCH IS WIDER (owner 2026-08-24: the project lead *"could also be a director or
         * writer, given it's high-level"*). Plot, world and cast are showrunner decisions, and a director
         * thinks about them differently from a writer — so the Project seat auditions both benches, in
         * catalog order, de-duplicated in case an agent appears under either role.
         */
        const directorBench = (c.roles || []).find((r) => r.role === 'director')?.flavors || [];
        const seen = new Set<string>();
        setLeads([...bench, ...directorBench]
          .filter((f) => !seen.has(f.agentId) && seen.add(f.agentId))
          .map((f) => ({ agentId: f.agentId, name: f.name, flavor: f.flavor, hint: f.hint })));
      })
      .catch(() => { /* catalog unavailable: report NOT hired rather than guessing hired */ });
    return () => { alive = false; };
  }, [crewIds]);
  /**
   * WHO HOLDS THE WRITER'S SEAT IS THE CREW'S BUSINESS, never a second field (BUGS.md 2026-08-23). The seat
   * was plain component state seeded with the house-default writer, so reopening a production showed whoever
   * the catalog happened to list first instead of the writer the creator hired — the pick read as lost.
   * Picking a writer has always RECRUITED them (see `onPickWriter` below), and recruiting IS joining this
   * production's crew, which is persisted; so the seat is read back FROM the crew rather than stored again
   * somewhere the two could disagree. The roster is in join order, so the LAST writer candidate on it is the
   * one hired most recently — deterministic when several writers are aboard, and right when the creator
   * changes their mind. A writer who leaves the crew simply vacates the seat.
   */
  const writerId = useMemo(() => {
    const bench = new Set(writers.map((w) => w.agentId));
    for (let i = crewIds.length - 1; i >= 0; i--) if (bench.has(crewIds[i])) return crewIds[i];
    return '';
  }, [writers, crewIds]);
  /** The project seat reads back from the crew the same way, over the wider bench — one rule, two seats. */
  const leadId = useMemo(() => {
    const bench = new Set(leads.map((w) => w.agentId));
    for (let i = crewIds.length - 1; i >= 0; i--) if (bench.has(crewIds[i])) return crewIds[i];
    return '';
  }, [leads, crewIds]);

  const [stage, setStage] = useState<StageKey>(initialStage || 'story');
  /** The latest `persist`, for callbacks declared above it (the script draft saves a new episode first). */
  const persistRef = useRef<() => Promise<string | null>>(async () => null);
  /** True once the creator has moved themselves — so auto-resume never yanks them off a stage they chose. */
  const stageChosen = useRef(false);
  /**
   * A STAGE CHANGE IS A SAVE POINT (owner 2026-08-23: "remember to auto-save and auto load saved when user
   * navigating the workflow tabs"). Autosave is debounced, so stepping from Story to Cast within a couple of
   * seconds of the last keystroke left the newest work unwritten until the timer happened to fire. Moving
   * stage now asks the autosave below to run IMMEDIATELY instead of on the debounce — the rule that an empty
   * open never creates a document still holds, because that check lives in the one place it always did.
   */
  const [pendingFlush, setPendingFlush] = useState(false);
  /** Bumped every time the episode is written, so server-reading children can re-ask. */
  const [saveTick, setSaveTick] = useState(0);
  /**
   * HOW MANY SHOTS THE SERVER HOLDS for this episode (BUGS.md 2026-09-26). The board always shows at least a
   * starter shot, and a new shot appears before autosave writes it; a server reader (the reference strip) must
   * not ask about a shot the server has never been told about. Taken from real responses only: the loaded
   * episode, and each save's returned document.
   */
  const [savedShotCount, setSavedShotCount] = useState(0);
  const goStage = useCallback((k: StageKey) => {
    stageChosen.current = true;
    setPendingFlush(true);
    setStage(k);
    onStageChange?.(k);          // the stage is a page, so it belongs in the URL
  }, [onStageChange]);

  /**
   * A stage arriving from the URL (back button, a shared link) wins over local state — including over the
   * RESUME decision. The guard used to be `initialStage !== stage`, so an explicit stage that happened to
   * equal the default never marked the choice as made, and auto-resume then moved the creator off the page
   * their own URL had named.
   */
  useEffect(() => {
    if (!initialStage) {
      /**
       * AN EPISODE ADDRESS WITH NO STAGE NAMES THE EPISODE (owner 2026-09-13: *"from project, I click episode,
       * it doesn't go to it"*).
       *
       * `/dashboard/{slug}` is how the explorer opens an episode, and it carries no stage segment — so this
       * effect used to return and leave the panel wherever it was. From the PROJECT surface that made the
       * click look like nothing had happened, because the project page is identical for every episode in a
       * series: the URL had changed, the content had not.
       *
       * Handing it back to RESUME rather than picking a stage here is deliberate — reopening an episode should
       * land on its first unfinished stage, which is the rule that already governs opening one from anywhere
       * else, and it is the effect below that knows how to work that out.
       */
      /* The same holds when Back returns to a stage-less address after the creator had stepped through the
         stages: the stage they left is no longer what the URL names, so resume decides again. */
      if (stage === 'project' || stageChosen.current) {
        stageChosen.current = false;
        setStage(resumeStage(statuses));
      }
      return;
    }
    stageChosen.current = true;
    if (initialStage !== stage) setStage(initialStage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow the URL, not our own echo of it
  }, [initialStage, openStoryboardId]);
  const [series, setSeries] = useState<{ id: string; title: string; episode: number; continuesFrom?: string } | null>(null);
  /**
   * THE PROJECT'S OWN DOCUMENT (RF3-LEVEL, owner 2026-08-24). The `series` state above is just the LINK an
   * episode carries; the project's high-level facts — plot, world, cast — live on the series doc and every
   * episode is written against them. Loaded when a project is known, saved on a debounce like the storyboard.
   */
  const [project, setProject] = useState<Series | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  /** The creator's store handle — read once, so a published episode can link to where it actually appears. */
  const [storeShortId, setStoreShortId] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    getMyStore().then((r) => { if (alive) setStoreShortId(r.store?.short_id || null); }).catch(() => { /* the toggle still works */ });
    return () => { alive = false; };
  }, []);
  /**
   * THE EPISODE'S OWN TITLE (owner 2026-08-24: *"let's have a title field for the episode when filling up the
   * episode's info"*). Empty means unnamed, and the server resolves an unnamed episode to `<project> ep.<n>`
   * on read — so a creator who never names one still sees a sensible name everywhere, and the default tracks
   * a project rename instead of freezing a copy of it.
   */
  const [episodeTitle, setEpisodeTitle] = useState('');
  /**
   * WHAT THIS EPISODE NARROWS TO (RF3-LEVEL C1). Empty means INHERITED, not blank — so we never seed these
   * from the project: copying the project's answers in would freeze them, and a later project edit would
   * silently stop reaching the episodes that had been "filled in" for them.
   */
  const [episodeSetting, setEpisodeSetting] = useState('');
  const [episodeFeaturing, setEpisodeFeaturing] = useState('');
  // Shovel B: 'story' = serialized micro-drama, 'hook' = a scroll-stopping UA ad hook (short + variation batch).
  const [mode, setMode] = useState<'story' | 'hook'>('story');
  // The chosen WORK STREAM (P1.5). Null = show the stream picker first (onboarding-by-stream). A picked stream seeds
  // the editor defaults (mode/duration/genre) + provisions that stream's preset crew. Cleared on reset/new project.
  const [stream, setStream] = useState<StreamDef | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // Shots (0-based) whose panel the last preview run could not draw — marked on the strip until the next run.
  const [previewFailed, setPreviewFailed] = useState<number[]>([]);
  // Which shot index is currently being re-made (null = none). Drives the per-shot spinner + button-disable.
  const [remakingIndex, setRemakingIndex] = useState<number | null>(null);

  // Templates are STREAM-SCOPED (a music-video creator must never be offered CEO Romance), so they refetch
  // whenever the stream changes rather than loading once with the Studio.
  useEffect(() => {
    if (!open) return;
    getStudioGenres(stream?.preset).then(setGenres).catch(() => { /* no templates -> the lightbulb hides */ });
  }, [open, stream?.preset]);

  useEffect(() => {
    if (!open || toggleGroups.length) return;
    getStudioToggles().then((r) => setToggleGroups(r.toggles)).catch(() => setError('Could not load the Studio.'));
    getStudioSpecialists().then(setSpecialists).catch(() => {});
    getMediaAliases().then((a) => { setAliases(a); setVideoAlias((cur) => cur || a.find((x) => x.recommended)?.key || a[0]?.key || ''); }).catch(() => {});
  }, [open, toggleGroups.length]);

  // Starters are STREAM-SCOPED (a music-video creator must never be offered CEO Romance), so they refetch
  // whenever the stream changes rather than loading once with the Studio.
  useEffect(() => {
    if (!open) return;
  }, [open, stream]);

  /**
   * Seed the editor's FORMAT DEFAULTS — length and shape. Not a question any more (RF3-NOSTREAM, owner
   * 2026-08-23: "we don't need it, it's a distraction — just landing on video studio should be enough, as it
   * already has phases"): landing in the Studio applies the house format and drops the creator straight into
   * the journey. Still called with a nature when one is KNOWN — the dashboard launch, or a reopened
   * production restoring what it was saved as.
   *
   * No crew is provisioned here any more either. Inferring the right crew from the idea is RF3-NOSTREAM slice
   * B; until then, recruiting on demand already works, so nothing is blocked by not guessing up front.
   */
  const pickStream = useCallback((s: StreamDef) => {
    setStream(s);
    setMode(s.mode);
    setDuration(s.duration);
    setError('');
  }, []);

  // Per-action costs are deliberately NOT surfaced any more (owner 2026-08-22) — the usage bar on the profile
  // is the one place a creator checks where they stand, and the server still fails fast with `needsTopUp`.

  const patchShot = useCallback((index: number, patch: Partial<StoryboardShot>) => {
    setShots((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    setSavedAt(false);
  }, []);

  const [drafting, setDrafting] = useState(false);
  // W13 format-fit advisories on the current draft (no cliffhanger / no hook / flat pacing). Cleared on re-draft.
  const [formatNotes, setFormatNotes] = useState<FormatIssue[]>([]);
  /**
   * THE WRITER'S PROPOSAL (RF3-STORY, owner-reported 2026-08-15: "can't see the button generating preview").
   * Develop used to write the shot list DIRECTLY: the creator clicked, the Story page showed nothing, and the
   * board had silently been replaced. The draft now lands here for REVIEW; only Approve commits it.
   */
  const [scriptDraft, setScriptDraft] = useState<ShotDraft[] | null>(null);
  /**
   * THE APPROVED SCRIPT, READ BACK OFF THE PRODUCTION (owner 2026-08-23: "after I approve the script for
   * story, and navigate to Cast, then go back, the Script is gone").
   *
   * Approval moves the beats into `shots` and clears the draft buffer — and Story used to render from that
   * buffer alone, so returning to the page showed an empty pane and the creator's writing looked lost. The
   * shots ARE the script, so the page projects them instead of remembering them separately: whatever is on
   * the production is what Story shows, on this visit and every later one. A live draft still wins while one
   * is actually under review.
   */
  const approvedScript = useMemo<ShotDraft[] | null>(() => {
    /**
     * PROJECT THE WHOLE SHOT BACK, not just its line.
     *
     * This mapped to `{ line }` alone, so the approved script was a strictly poorer document than the draft it
     * came from: dialogue, coverage and sound appeared while the proposal was live and disappeared the moment
     * it was accepted. The creator would reasonably read that as the work having been lost — and until the
     * approval step was fixed to persist these, it actually had been.
     */
    const beats = shots
      .filter((s) => s.line?.trim())
      .map((s) => ({
        line: s.line as string,
        ...(s.weight ? { weight: s.weight } : {}),
        ...(s.sfx ? { sfx: s.sfx } : {}),
        ...(s.camera ? { camera: s.camera } : {}),
        ...(s.transition ? { transition: s.transition } : {}),
        ...(s.dialogue?.length
          ? { dialogue: s.dialogue.map((d) => ({ character: d.character || '', line: d.line })) }
          : {}),
      }));
    return beats.length ? beats : null;
  }, [shots]);
  const scriptView = scriptDraft || approvedScript;
  /** What the creator was doing when they ran out of credits — drives the top-up modal at the moment intent peaks. */
  const [topUp, setTopUp] = useState<{ action: string } | null>(null);

  const draftStory = useCallback(async () => {
    if (!idea.trim()) return;
    setDrafting(true); setError('');
    try {
      // THE EPISODE'S ID TRAVELS WITH THE DRAFT (BUGS.md 2026-09-28): without it the server cannot tell this is the
      // free first storyboard and charged the script. A brand-new episode is saved first so it has one.
      // Developing a script IS choosing the Story stage: mark it, or the first save's new id triggers the load-time
      // resume and a brand-new series (no plot yet) jumps the creator to the series page mid-draft.
      stageChosen.current = true;
      const draftSbId = storyboardIdRef.current || await persistRef.current().catch(() => null);
      const { shots: drafted, music: scored, needsTopUp, formatIssues } = await draftBeatSheet(
        // The genre is the PROJECT's now and is resolved server-side from the series; it is sent only so a
        // production whose series has none yet still drafts as it did.
        idea, 4, project?.genre || '', series?.continuesFrom, series?.id, writerId || undefined,
        { setting: episodeSetting, featuring: episodeFeaturing, episodeNumber: series?.episode, storyboardId: draftSbId },
      );
      if (needsTopUp) { setTopUp({ action: 'develop the story' }); return; }
      /* AN EMPTY DRAFT IS A FAILURE THE CREATOR MUST SEE (BUGS.md 2026-09-27). The server answers [] with no
         charge when the writer's reply was unusable; the button used to simply stop spinning, so the press looked
         ignored and nobody knew to try again. */
      if (!drafted.length) {
        setError('The writer came back empty-handed this time. Please try again — you were not charged.');
        showError('No script this time', 'The writer came back empty-handed. Try again — you were not charged.');
        return;
      }
      setScriptDraft(drafted);
      /**
       * The COMPOSER's spans, kept beside the draft. Music belongs to the episode rather than to any one shot
       * — it runs across several — which is why it arrives separately and lands on the storyboard's own
       * `music_spans` rather than being folded onto a beat.
       */
      if (scored?.length) setMusicSpans(scored);
      // W13: advisory only — surfaced BEFORE render spend so a flat episode can be re-drafted for free.
      setFormatNotes(formatIssues || []);
    } catch { setError('Could not draft the story. Please try again.'); }
    finally { setDrafting(false); }
  }, [idea, project?.genre, series, writerId, episodeSetting, episodeFeaturing, showError]);

  /**
   * REWRITE ONE BEAT IN PLACE (owner 2026-08-23: the creator can "regenerate or edit"). A generated draft you
   * cannot touch is a slot machine — the only recourse would be another whole pass for one clumsy line.
   *
   * Which store the edit lands in follows which script is on screen: a live proposal edits the draft buffer,
   * and an APPROVED beat edits the shot itself, because the approved script is those shots projected back.
   * Editing an approved beat therefore marks the production dirty so autosave carries it.
   */
  const editBeat = useCallback((index: number, line: string) => {
    if (scriptDraft) {
      setScriptDraft((d) => (d ? d.map((b, i) => (i === index ? { ...b, line } : b)) : d));
      return;
    }
    setShots((prev) => prev.map((s, i) => (i === index ? { ...s, line } : s)));
    setSavedAt(false);
  }, [scriptDraft]);

  /** Approve the writer's draft into the production. The ONLY destructive step — so it carries the confirm. */
  const approveScript = useCallback(async () => {
    if (!scriptDraft?.length) return;
    const hasWork = shots.some((s) => s.line?.trim() || s.startFramePath || (s.dialogue && s.dialogue.length));
    if (hasWork) {
      const ok = await showConfirm({
        title: 'Replace your current shots?',
        description: 'Approving this script writes a new set of shots. Anything already on the board will be replaced.',
        confirmText: 'Replace them',
        cancelText: 'Keep what I have',
        variant: 'destructive',
      });
      if (!ok) return;
    }
    /**
     * KEEP EVERYTHING THE WRITER WROTE (owner 2026-08-30: *"the writer's draft missing music, dialog, camera
     * angle — only high level plot is not enough"*).
     *
     * This was `{ line: d.line, toggles: {} }`, which threw away every craft decision in the draft at the
     * moment of approval. The writer was already drafting sound and pacing; both were discarded here, so the
     * complaint was doubly true — the draft was thin AND what it did produce never reached a shot.
     *
     * The speaker binding is the point of casting a voice at project level: a drafted line says "MARA", and
     * that name resolves to the character the creator cast, and therefore to their voice. Matched on trimmed
     * lowercase name because the writer is told to use the cast's own names but is still a model — an
     * unmatched speaker keeps their line and simply has no voice yet, which is recoverable, whereas dropping
     * the line would not be.
     */
    const voiceOf = (who: string) => {
      const match = (project?.bible?.cast || []).find(
        (c) => c.name.trim().toLowerCase() === who.trim().toLowerCase(),
      );
      return match?.voice_id || undefined;
    };
    setShots(scriptDraft.map((d) => ({
      line: d.line,
      toggles: {},
      ...(d.weight ? { weight: d.weight } : {}),
      ...(d.continuesScene !== undefined ? { continuesScene: d.continuesScene } : {}),
      ...(d.sfx ? { sfx: d.sfx } : {}),
      ...(d.camera ? { camera: d.camera } : {}),
      ...(d.transition ? { transition: d.transition } : {}),
      // WHO and WHERE, linked by name on the server (CONSIST-1): what lets every keyframe key the cast's face.
      ...(d.character_ids?.length ? { character_ids: d.character_ids } : {}),
      ...(d.location_id ? { location_id: d.location_id } : {}),
      ...(d.dialogue?.length
        ? { dialogue: d.dialogue.map((l) => ({ character: l.character, line: l.line, voiceId: voiceOf(l.character) })) }
        : {}),
    })));
    setSelectedShot(0); setSavedAt(false); setScriptDraft(null);
    // Approved → the shot BOARD, which is what the beats just became. It used to go to Cast; with cast and
    // crew now the project's (owner 2026-08-24), the next thing an EPISODE needs is its shots.
    goStage('shots');
  }, [scriptDraft, shots, showConfirm, goStage, project?.bible?.cast]);

  // Shovel B: draft a BATCH of ad-hook variations (each a distinct angle) the marketer can A/B, then pick one.


  const addShot = useCallback(() => setShots((prev) => [...prev, emptyShot()]), []);
  const removeShot = useCallback((index: number) => setShots((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev)), []);
  /** Copy a shot in place. Respects the cap, so duplicate can never push an episode over its shot limit. */
  const duplicateShot = useCallback((index: number) => setShots((prev) => (
    prev.length >= MAX_SHOTS ? prev : [...prev.slice(0, index + 1), { ...prev[index] }, ...prev.slice(index + 1)]
  )), []);
  const reorderShots = useCallback((from: number, to: number) => {
    setShots((prev) => { const a = [...prev]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
    setSelectedShot(to); setSavedAt(false);
  }, []);

  const assistShot = useCallback(async (index: number, agentId?: string) => {
    setAssistingIndex(index);
    setError('');
    try {
      const priorShots = shots.slice(0, index).map((s) => ({ line: s.line, toggles: s.toggles, camera: s.camera, weight: s.weight }));
      const { suggestion, needsTopUp } = await assistShotApi({
        idea,
        shotIndex: index,
        priorShots,
        currentLine: shots[index]?.line,
        // The creator's own direction for this shot. Sending it is what makes the inspector's one prose field
        // mean something: "push in slowly as she realises" now arrives as a structured push, not as nothing.
        direction: shots[index]?.freeText,
        castNames: cast.map((c) => c.name).filter(Boolean),
        wantsMusic: sound?.kind === 'song' || sound?.kind === 'instrumental',
        /**
         * THE EPISODE ITSELF, so the server can resolve the project's own people, place, objects, voices and
         * sound state for this shot (owner 2026-09-13). Those names are load-bearing: a shot that says "Mara"
         * resolves to her locked face and voice, and one that says "a woman" resolves to a stranger — so the
         * copilot has to be given the names that work rather than left to invent plausible ones.
         */
        storyboardId: storyboardId || undefined,
        agentId,
      });
      if (needsTopUp) { setTopUp({ action: 'ask a specialist' }); return; }
      // The copilot now proposes the whole shot, so the merge takes the whole shot — reading a suggestion that
      // set coverage, pacing and sound for its line alone was the old behaviour dressed up. See applySuggestion
      // for what is and is not overwritten.
      if (suggestion) setShots((prev) => prev.map((s, i) => (i === index ? applySuggestion(s, suggestion) : s)));
    } catch {
      setError('AI Assist is unavailable right now.');
    } finally {
      setAssistingIndex(null);
    }
  }, [idea, shots, cast, sound, storyboardId]);

  /**
   * Put an earlier take back. Free — the clip already exists in Storage, so there is no affordability check
   * and no charge; the server just swaps which take is live and archives the one it replaced.
   */
  const restoreOneTake = useCallback(async (index: number, clipPath: string) => {
    if (!storyboardId) return;
    setError('');
    try {
      const sb = await restoreTakeApi(storyboardId, index, clipPath);
      if (sb?.shots) setShots(sb.shots);
      showInfo('Take restored', 'That earlier take is back in the cut.');
    } catch {
      setError('Could not restore that take.');
      showError('Couldn’t restore', 'That take could not be restored. Please try again.');
    }
  }, [storyboardId, showError, showInfo]);

  /** A create already in flight, so two callers on an id-less production share one episode rather than two. */
  const creating = useRef<Promise<{ id: string; series_id?: string | null; episode_number?: number | null }> | null>(null);

  /**
   * THE EPISODE'S ID, READ FROM A REF RATHER THAN A CLOSURE (BUGS.md 2026-09-13).
   *
   * `persist` is rebuilt on every render and captured by the autosave, by stage navigation, and by every
   * action that needs an id first. A capture taken before the id arrived still said "no episode yet", so the
   * next save CREATED a second one: the creator ended up with two ep.1s, their work written to the newer and
   * a reload opening the older, which is how a shot they had just written appeared to vanish. Found by the
   * creator journey, whose explorer showed the duplicate plainly.
   *
   * The ref is written in the same breath as the state, so whichever closure runs reads the CURRENT episode.
   */
  const storyboardIdRef = useRef<string | null>(null);
  const rememberStoryboardId = useCallback((id: string | null) => {
    storyboardIdRef.current = id;
    setStoryboardId(id);
    if (!id) setSavedShotCount(0);
  }, []);

  const persist = useCallback(async (): Promise<string | null> => {
    const payload = {
      // THE IDEA IS NOT THE TITLE (BUGS.md 2026-08-23). This used to send `title: idea`, which named every
      // production with its own story text AND made a reload write the stored title back over what the
      // creator was typing — losing their premise at the exact moment they hired a writer. The premise now
      // has its own field, and the NAME is not ours to set: the server defaults it to `<series> ep.<n>` and
      // the explorer is the one place it is renamed. What we DO send is a title the creator typed into the
      // episode's own field — never the idea, and never a value we invented (an empty string leaves the
      // episode unnamed, which is what makes the `<project> ep.<n>` default keep tracking a project rename).
      ...(episodeTitle.trim() ? { title: episodeTitle.trim() } : {}),
      setting: episodeSetting,
      featuring: episodeFeaturing,
      idea, duration_seconds: duration, sound, music_spans: musicSpans, shots,
      video_alias: videoAlias || null, continuity_mode: continuityMode,
      // SERIES IS SERVER-OWNED, exactly like the crew-chat link below. Every production now BELONGS to a
      // series (RF3-SCHEMA) — the server mints one on create when we do not name it. Autosave used to resend
      // `series_id: series?.id || null` on every save, and because the handler treats a present-but-null field
      // as "set it to null", the second save wrote null over the server-minted link and cut the production off
      // from its own cast and crew. Same defect as the conversation_id bug of 2026-08-16, one field over.
      // Only send the key when we actually HAVE a series; `undefined` leaves the stored value untouched.
      ...(series?.id ? { series_id: series.id, episode_number: series.episode || 1 } : {}),
    };
    // The crew-chat link is SERVER-OWNED: a production born inside a chat carries that chat in at CREATE, and
    // from then on only recruiting changes it. Autosave used to resend it on every save — which wrote null over
    // a stamped link and quietly cut a production off from its crew (BUGS.md 2026-08-16).
    /**
     * ONE CREATE, HOWEVER MANY CALLERS (BUGS.md 2026-09-13).
     *
     * `persist` is called by the autosave AND by every action that needs an id first — preview, render,
     * recruiting, re-make. Two of those overlapping on a production that has no id yet BOTH saw
     * `storyboardId === null` (it is state, so it updates a tick later) and BOTH created an episode. The
     * creator ended up with two ep.1s, their work written to one of them, and a reload opening the other —
     * which is exactly how "I wrote a shot and it was gone" happens, and a likely source of the preview
     * route's "Storyboard id is required" (a second caller still holding null).
     *
     * So a create in flight is SHARED: the second caller awaits the first rather than starting its own.
     */
    let sb;
    // The REF, never the captured state: see `storyboardIdRef` for the duplicate-episode defect this prevents.
    const currentId = storyboardIdRef.current;
    if (currentId) {
      sb = await updateStoryboard(currentId, payload);
    } else {
      // The NATURE of the production travels with the CREATE only: the server stamps it on the series it mints
      // here, and a series has one nature from then on (changing it is a deliberate act, not an autosave).
      if (!creating.current) {
        creating.current = createStoryboard({ ...payload, conversation_id: conversationId || null, stream: stream?.preset || null })
          // Told inside the shared create, so however many callers await it the host hears about it ONCE.
          .then((made) => { onEpisodeCreated?.(made.slug || made.id); return made; })
          .finally(() => { creating.current = null; });
      }
      sb = await creating.current;
    }
    rememberStoryboardId(sb.id);
    // The shots just written ARE what the server now holds (a save replaces the list whole).
    setSavedShotCount(payload.shots.length);
    // Adopt the series the server minted on create, so the next autosave knows about it (and so Cast/Crew,
    // which hang off the series, resolve for a brand-new production rather than looking empty).
    if (sb.series_id && !series?.id) setSeries({ id: sb.series_id, title: '', episode: sb.episode_number || 1 });
    return sb.id;
    // `storyboardId` is deliberately NOT a dependency: the decision reads the ref, so a rebuild on every
    // id change would only churn the callback identity the autosave effect watches.
  }, [idea, episodeTitle, episodeSetting, episodeFeaturing, duration, sound, musicSpans, shots, conversationId, series, videoAlias, continuityMode, stream, rememberStoryboardId, onEpisodeCreated]);

  persistRef.current = persist;

  /**
   * THE SERIES PAGE NEEDS A SERIES (BUGS.md 2026-09-27: *"i can't type on /dashboard/new/project"*). Every edit on
   * that page is a write to the series, and an episode that was never saved has none, so each keystroke was
   * dropped. Arriving there without one saves the episode once: the server mints the series, `persist` adopts
   * it, and the host moves the address off /new. The page reads as loading until then.
   */
  const mintingSeries = useRef(false);
  useEffect(() => {
    // ONLY for an address that says the episode is unsaved (BUGS.md 2026-09-27, the creation e2e run): reloading
    // /dashboard/<slug>/project briefly had no series and no id while the episode loaded, and this minted a second,
    // empty series every time. Guessing from load state raced; the address is the fact.
    if (!unsavedEpisode) return;
    if (stage !== 'project' || series?.id || openStoryboardId || storyboardIdRef.current || mintingSeries.current) return;
    mintingSeries.current = true;
    persist()
      .catch(() => showError('Could not start your series', 'Nothing was saved. Please try again in a moment.'))
      .finally(() => { mintingSeries.current = false; });
  }, [unsavedEpisode, stage, series?.id, openStoryboardId, persist, showError]);

  /**
   * RECRUITING IS JOINING THE CREW (owner 2026-08-16) — the one door every hire in the Studio goes through:
   * writer, director, crew strip, legal. The server creates this production's crew chat on the FIRST recruit,
   * stamps the link on the production itself, and seats the agent. Free, idempotent, capped at a full crew.
   *
   * The single fallback is a Studio opened from inside a chat before anything is saved: there is no production
   * to hang a crew chat on yet, so the agent joins the chat we are standing in.
   */
  const recruit = useCallback(async (agentId: string, replacesAgentId?: string) => {
    if (!storyboardId && conversationId) {
      await addSuggestedAgent(conversationId, agentId);
    } else {
      /**
       * A PROJECT-LEVEL RECRUIT MUST NOT MINT AN EPISODE (owner 2026-08-29: updating the agent on the project
       * "jumps to episode setup"). Persisting here created a document the creator never asked for, and the
       * new id then triggered auto-resume onto that episode's Story page. On the project surface we name the
       * SERIES and let the server find an episode the project already has.
       */
      const onProject = stage === 'project' || stage === 'cast' || stage === 'director';
      const target = storyboardId
        ? { storyboardId }
        : onProject && series?.id
          ? { seriesId: series.id }
          : { storyboardId: (await persist()) || '' };
      if (!target.storyboardId && !target.seriesId) return;
      const res = await recruitCrewAgent(target, agentId, replacesAgentId);
      // The crew chat may have just been FOUNDED by this call — hand the whole conversation back so the
      // right pane can show it live, without waiting for a reload to refetch the chat list.
      onCrewChatResolved?.({ conversationId: res.conversationId, conversation: res.conversation });
    }
    // Show them on the crew immediately — the roster this panel is given comes from a mounted chat, which a
    // production opened from the dashboard does not have (the right pane arrives in the next slice).
    // The local roster has to SWAP too, or the seat keeps showing the agent who just left until a reload.
    setRecruited((prev) => {
      const without = replacesAgentId ? prev.filter((id) => id !== replacesAgentId) : prev;
      return without.includes(agentId) ? without : [...without, agentId];
    });
  }, [storyboardId, conversationId, persist, onCrewChatResolved, stage, series?.id]);

  /**
   * TAKE SOMEBODY OFF, and keep the crew CHAT in step (owner 2026-09-13: *"ensure chat group is dynamic
   * update crew chat group members based on hiring"*).
   *
   * The rule this follows is the one hiring already follows: the server owns the roster, and whatever it
   * hands back becomes what the right pane shows. Handing the updated conversation straight to
   * `onCrewChatResolved` is what makes the change visible immediately instead of after a reload — the seats
   * and the chat read from the same answer, so they cannot disagree.
   *
   * A refusal (the server will not empty a crew) surfaces on the seat that asked, in its own words.
   */
  const dismiss = useCallback(async (agentId: string) => {
    const onProject = stage === 'project' || stage === 'cast' || stage === 'director';
    const target = storyboardId
      ? { storyboardId }
      : onProject && series?.id ? { seriesId: series.id } : null;
    if (!target) return;
    const res = await dismissCrewAgent(target, agentId);
    onCrewChatResolved?.({ conversationId: res.conversationId, conversation: res.conversation });
    setRecruited((prev) => prev.filter((id) => id !== agentId));
  }, [storyboardId, stage, series?.id, onCrewChatResolved]);

  /** Reset the editor to a blank video (optionally linked to a series episode). */
  /* `resetEditor` (start a blank episode linked to a series) went with the Library tab that called it. The
     launchpad's "add to an existing series / start a new one" flow (RF3-POLISH) will need it back — it is in
     git history rather than kept dead here. */

  /** Load an existing episode/storyboard into the editor. */
  const loadEpisode = useCallback((sb: import('../../services/studioService').Storyboard) => {
    // The premise comes back from the field that holds it. A production saved before the split has no
    // `idea` of its own — the server hands its old title back AS the idea, so nothing a creator wrote is
    // lost and the title never lands in this box again (BUGS.md 2026-08-23).
    setIdea(sb.idea || '');
    // A resolved default (`<project> ep.<n>`) is not a NAME the creator chose, so it must not land in the
    // field as though they had typed it — otherwise saving would freeze that default into the document.
    setEpisodeTitle(sb.title && sb.idea !== sb.title && !/ ep\.\d+$/.test(sb.title) ? sb.title : '');
    setPublished(!!sb.published);
    setEpisodeSetting(sb.setting || '');
    setEpisodeFeaturing(sb.featuring || '');
    setDuration(sb.duration_seconds || 30);
    setSound(sb.sound || { kind: 'none', mood: null });
    setMusicSpans(sb.music_spans || []);
    setVideoAlias(sb.video_alias || '');
    setContinuityMode(sb.continuity_mode || 'auto');
    setShots(sb.shots?.length ? sb.shots : [emptyShot()]);
    setSavedShotCount(sb.shots?.length || 0);
    rememberStoryboardId(sb.id); setStatus(sb.status); setFinalPath(sb.status === 'ready' ? sb.final_video_path || null : null); setCaptions(sb.captions || null); setSelectedShot(0);
    setSeries(sb.series_id ? { id: sb.series_id, title: '', episode: sb.episode_number } : null);
    // The production remembers WHAT KIND of video it is: the nature comes back on its series, so a reopened
    // product ad is a product ad again — ad placeholder, ad-scoped starters, ad crew (BUGS.md 2026-08-22).
    // Only the mode follows from the nature; the saved length wins over the stream's default.
    const nature = STREAMS.find((x) => x.preset === sb.stream) || null;
    if (nature) { setStream(nature); setMode(nature.mode); }
    setSavedAt(true); setError('');
    // The production carries its crew chat; the right pane opens on it (null = nobody recruited yet).
    onCrewChatResolved?.({ conversationId: sb.conversation_id || null });
  }, [onCrewChatResolved, rememberStoryboardId]);

  // Dashboard launch: when opened to reopen a production, fetch + load it; otherwise seed the house format and
  // go. Runs once per open (keyed on the launch inputs), never in the normal in-chat open.
  useEffect(() => {
    if (!open) return;
    if (openStoryboardId) { getStoryboard(openStoryboardId).then(loadEpisode).catch(() => setError('Could not open that production.')); return; }
    // THE HOUSE FORMAT, applied without asking (RF3-NOSTREAM A). Micro-drama's 60s episode is the format the
    // product is built on (PRODUCT.md; the craft KB's 60–120s band), and the pipeline does not branch by kind
    // of video anyway — so landing here means landing IN the journey, one field away from a story.
    // A brand-new production has no crew yet — say so, so the right pane rests on contacts rather than
    // keeping the previous production's crew chat open beside an unrelated board.
    onCrewChatResolved?.({ conversationId: null });
    if (initialStream) pickStream(initialStream);
    else pickStream(STREAMS.find((x) => x.preset === 'micro_drama') || STREAMS[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- launch inputs only; pickStream/loadEpisode are stable
  }, [open, openStoryboardId, initialStream]);

  /**
   * AUTOSAVE (owner 2026-08-16): the draft saves ITSELF — a Save button was homework the machine can do.
   * Debounced on the dirty flag; only once there is real substance, so an empty open never creates a doc.
   */
  const save = useCallback(async () => {
    setSaving(true); setError('');
    try {
      await persist();
      setSavedAt(true);
      // A COUNTER, not a flag: anything downstream that asks the SERVER about this episode needs to know a
      // write landed, and needs to hear it again on the next one. The shot inspector's reference strip is the
      // first such reader — without this it answers from the state the server had a moment BEFORE a pick, and
      // then has no reason to ask again (found by the creation-flow walk 2026-09-12).
      setSaveTick((n) => n + 1);
    } catch { setError('Could not save. Please try again.'); }
    finally { setSaving(false); }
  }, [persist]);

  const generate = useCallback(async (quality: 'draft' | 'final' = 'final') => {
    /*
     * CONSENT BEFORE THE BIGGEST SPEND IN THE PRODUCT.
     *
     * Ambient per-action pricing was removed on 2026-08-22 so a creator is not pricing every click — but the
     * render is different in kind: it is the single most expensive action here, it charges per shot, and it is
     * not reversible once the clips start generating. Removing the old always-on price bar left Generate with
     * NO consent step at all (it called the server directly and only reacted to `needsTopUp` afterwards), which
     * is the one thing the media rules do not allow: the human approves before we spend.
     *
     * So this is a CONFIRM AT THE MOMENT OF SPENDING, not a price tag on the screen. It names how many shots
     * will be rendered rather than a WP figure — the count is what the creator actually controls, and the usage
     * bar on their profile is where "how much have I got left" lives.
     */
    const shotCount = shots.filter((sx) => sx.line?.trim()).length;
    /**
     * The two speeds are consented to DIFFERENTLY because they are different decisions. A draft is a cheap
     * look, so the confirmation says what it is FOR and what it is not (publishable). A final render is the
     * expensive, finishing act, and it warns that drafted shots will be made again — otherwise a creator who
     * drafted the whole episode would be surprised by the size of the charge.
     */
    const isDraft = quality === 'draft';
    const redoing = shots.filter((sx) => sx.draft && sx.clipPath).length;
    const ok = await showConfirm({
      title: isDraft
        ? (shotCount === 1 ? 'Draft this shot?' : `Draft all ${shotCount} shots?`)
        : (shotCount === 1 ? 'Render this shot?' : `Render all ${shotCount} shots?`),
      description: isDraft
        ? 'A quick, low-cost pass so you can see whether the shots work. The quality is rough and it cannot be published — render it properly when you are happy.'
        : redoing
          ? `This spends credits and makes your finished cut. ${redoing} drafted shot${redoing > 1 ? 's are' : ' is'} rendered again at full quality; your draft takes are kept.`
          : 'This spends credits and starts generating your video. Shots already rendered are not charged again.',
      confirmText: isDraft ? 'Draft it' : 'Render it',
      cancelText: 'Not yet',
    });
    if (!ok) return;

    setStarting(true); setSaving(true); setError(''); setFinalPath(null);
    try {
      const id = await persist(); // save the latest edits first
      if (!id) return;
      const res = await renderStoryboard(id, quality);
      if (res.providerDown) { setError('Video generation is temporarily unavailable — please try again in a moment.'); showError('Studio busy', 'Video generation is temporarily unavailable — please try again in a moment.'); return; }
      if (res.needsTopUp) { setTopUp({ action: quality === 'draft' ? 'draft this video' : 'render this video' }); return; }
      setStatus('generating'); setRenderedCount(0); // the poll takes over
      // Confirm the render actually STARTED — so even if the user navigates away, they know it's cooking and will
      // land in the Library. The poll toasts again on done/failed.
      showInfo(
        quality === 'draft' ? 'Drafting your video…' : 'Rendering your video…',
        'This can take a couple of minutes. We’ll let you know when it’s ready.',
      );
    } catch {
      setError('Could not start the render. Please try again.');
      showError('Couldn’t start', 'Could not start the render. Please try again.');
    } finally {
      setSaving(false); setStarting(false);
    }
  }, [persist, showError, showInfo, shots, showConfirm]);

  // PRE-GENERATION preview — save the latest edits, then generate cheap keyframes (a comic strip) so the creator can
  // check framing/look/casting before paying for the full render. `force` refreshes existing panels.
  const runPreview = useCallback(async (force: boolean, redraw?: number[]) => {
    setPreviewing(true); setError(''); setPreviewFailed([]);
    try {
      const id = await persist();
      /**
       * A DEAD CLICK IS WORSE THAN AN ERROR (2026-09-13). This used to `return` silently when the episode had
       * no id yet: the button span, stopped, and said nothing, so the creator pressed it again. Whatever
       * leaves `persist` without an id — a save that did not land, an episode switched underneath — the honest
       * answer is to say so and let them retry. The server sees the same situation as a 400 it cannot explain
       * ("Storyboard id is required"), which is the shape this error has been arriving in.
       */
      if (!id) {
        setError('Your episode has not saved yet. Give it a moment and try again.');
        showError('Not saved yet', 'Your episode has not saved yet. Give it a moment and try again.');
        return;
      }
      const res = await previewStoryboardFrames(id, force, redraw);
      if (res.providerDown) { setError('Preview is temporarily unavailable — please try again in a moment.'); showError('Studio busy', 'Preview is temporarily unavailable — please try again in a moment.'); return; }
      if (res.needsTopUp) { setTopUp({ action: 'preview the episode' }); return; }
      // Merge the returned preview paths back onto the shots so the strip updates without a full reload.
      setShots((prev) => prev.map((s, i) => {
        const hit = res.results.find((r) => r.index === i);
        return hit && hit.previewFramePath ? { ...s, previewFramePath: hit.previewFramePath, previewQuality: hit.previewQuality ?? null } : s;
      }));
      /*
       * SAY WHAT IT DID, especially when it did nothing or failed. The server redraws only the panels that are
       * missing or out of date (so an unchanged press is free), and reports each panel it could not draw — those
       * are named here and marked on the strip rather than passing as "up to date" (BUGS.md 2026-09-27).
       */
      const outcome = describePreviewOutcome(res);
      setPreviewFailed(outcome.failedIndexes);
      if (outcome.tone === 'error') { setError(outcome.body); showError(outcome.title, outcome.body); }
      else showInfo(outcome.title, outcome.body);
    } catch { setError('Could not preview the episode. Please try again.'); showError('Preview failed', 'Could not preview the episode. Please try again.'); }
    finally { setPreviewing(false); }
  }, [persist, showError, showInfo]);

  // RE-MAKE a single shot — save edits, regenerate + re-charge (directing + the clip) for JUST that shot, then poll
  // getStoryboard until that shot's new clip lands. The rest of the episode is untouched.
  const remakeOneShot = useCallback(async (index: number) => {
    setRemakingIndex(index); setError('');
    try {
      const id = await persist();
      if (!id) return;
      const res = await remakeShotApi(id, index);
      if (res.providerDown) { setError('Video generation is temporarily unavailable — please try again in a moment.'); showError('Studio busy', 'Video generation is temporarily unavailable — please try again in a moment.'); return; }
      if (res.needsTopUp) { setTopUp({ action: 're-make this shot' }); return; }
      // The server bumped the shot version + cleared its clip; the render sweep regenerates only that shot. Poll it.
      setStatus('generating'); setFinalPath(null);
      showInfo('Re-making that shot…', 'We’ll refresh it when the new take is ready.');
    } catch { setError('Could not re-make that shot. Please try again.'); showError('Couldn’t re-make', 'Could not re-make that shot. Please try again.'); }
    finally { setRemakingIndex(null); }
  }, [persist, showError, showInfo]);

  // Poll while a render is in flight — surface per-shot progress, then the finished video.
  useEffect(() => {
    if (status !== 'generating' || !storyboardId) return;
    let alive = true;
    const tick = async () => {
      try {
        const sb = await getStoryboard(storyboardId);
        if (!alive) return;
        setRenderedCount((sb.shots || []).filter((s) => s.clipPath).length);
        // Sync each shot's freshly-rendered clip path (and preview) back onto the working copy so thumbnails update —
        // matters for a single-shot re-make, where only one clip changes. Preserve the user's in-progress line/toggle edits.
        if (sb.shots?.length) setShots((prev) => prev.map((s, i) => {
          const srv = sb.shots[i];
          return srv ? { ...s, clipPath: srv.clipPath, clipFailed: srv.clipFailed, draft: srv.draft, previewFramePath: srv.previewFramePath ?? s.previewFramePath, version: srv.version } : s;
        }));
        if (sb.status === 'ready' || sb.status === 'failed') {
          setStatus(sb.status);
          if (sb.status === 'ready' && sb.final_video_path) {
            // The path, not a resolved URL: this same tick moves `status` off `generating` and tears this poll
            // down, so a URL resolved here landed after `alive` went false and the film was lost (BUGS.md 2026-09-27).
            setFinalPath(sb.final_video_path);
            // Captions arrive WITH the cut, from the same poll — there is nothing extra to fetch.
            setCaptions(sb.captions || null);
            // Notify on completion even if the user navigated away from the Create view (the toast is app-level).
            showSuccess('Your video is ready', 'Find it in your Library, or scroll down to watch it here.');
          } else if (sb.status === 'failed') {
            setError('That render didn’t come together this time — tap Generate to try again.');
            showError('Render didn’t come together', 'It happens sometimes — tap Generate to try again. You weren’t charged for the miss.');
          }
        }
      } catch { /* transient — keep polling */ }
    };
    const h = setInterval(tick, 4000);
    tick();
    return () => { alive = false; clearInterval(h); };
  }, [status, storyboardId, showSuccess, showError]);


  /** One shot's editor card (shared by the list view and the canvas side-editor). */
  /* The per-shot editor card is gone: a shot is edited ON its node (the line is a widget, the actions are on
     the selection toolbar). ShotCard remains in the tree for the docked list that no longer exists — delete it
     with the rest of that dead path. */

  // "busy" = the whole span from click through kickoff through the render poll — drives the button spinner + label so
  // the click is acknowledged IMMEDIATELY (the fix for "clicked Generate, nothing happened").
  useEffect(() => {
    if (!open) return;
    // Nothing to flush once the document is safely written — drop the request rather than letting it make the
    // creator's NEXT keystroke save instantly.
    if (savedAt || saving) { if (savedAt && pendingFlush) setPendingFlush(false); return; }
    /**
     * WHAT COUNTS AS WORTH SAVING. `freeText` is here because the inspector's main writing field — *Direct
     * this shot* — writes it, and without it a creator could write a paragraph of direction, reload, and find
     * it gone: the episode never saved, because nothing it checked had changed. The rule is meant to stop an
     * EMPTY open creating a document, not to discard writing (found by the creation-flow walk 2026-09-12).
     */
    const hasSubstance = idea.trim() || shots.some(isWrittenShot);
    if (!hasSubstance) return;
    const t = window.setTimeout(() => { setPendingFlush(false); void save(); }, pendingFlush ? 0 : AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [open, savedAt, saving, idea, shots, save, pendingFlush]);

  const rendering = status === 'generating';
  const busy = rendering || starting;
  // A storyboard is renderable once at least one shot has a description or a start frame — otherwise Generate would
  // kick off a render that can only fail. Gate the button on that and guide the user to the missing step instead.
  const canRender = shots.some(isWrittenShot);
  // The footer speaks to the STAGE the creator is on — "describe a shot" on the Story page was nonsense.
  /*
   * The footer stays as a quiet STATUS STRIP — no buttons (owner 2026-08-16: "don't remove the footer, but
   * hide the buttons"). Saving is automatic and Generate lives on the board, so the row's job is telling the
   * creator where they stand: what this stage expects, whether the render is moving, and that their work is
   * safely saved without them doing anything.
   */
  const footerHint = stage === 'shots'
    ? (!canRender ? 'Say what happens in a shot and it can be filmed' : 'Make the film when you are ready')
    : 'Your work saves itself — pick up where you left off any time';
  const footer = (
    <div className="flex items-center gap-2">
      {starting ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-secondary-500 dark:text-secondary-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Starting your render…
        </span>
      ) : rendering ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-secondary-500 dark:text-secondary-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering shot {Math.min(renderedCount + 1, shots.length)} of {shots.length}…
        </span>
      ) : (
        <span className="text-xs text-secondary-400 dark:text-secondary-500">{footerHint}</span>
      )}
      {/* Autosave earns trust by SAYING it happened — quietly, on the right. */}
      <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-secondary-400 dark:text-secondary-500">
        {saving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</> : savedAt ? <><Check className="h-3 w-3" /> Saved</> : null}
      </span>
    </div>
  );

  /* The journey's state, derived from the document — so the stepper can guide without a second source of truth. */
  /**
   * LOAD THE PROJECT the open episode belongs to. `listSeries` is the only read we have and it is a small,
   * owner-scoped list, so it costs one call per project change rather than a new endpoint. A project the
   * server has just minted may not be in the list yet, which reads as "not loaded" rather than as an error.
   */
  useEffect(() => {
    const id = series?.id;
    if (!id) { setProject(null); return; }
    if (project?.id === id) return;
    let alive = true;
    setProjectLoading(true);
    listSeries()
      .then((all) => { if (alive) setProject(all.find((x) => x.id === id) || null); })
      .catch(() => { if (alive) setProject(null); })
      .finally(() => { if (alive) setProjectLoading(false); });
    return () => { alive = false; };
  }, [series?.id, project?.id]);

  /**
   * Persist a project edit. Debounced like the storyboard's autosave — a creator typing a premise should not
   * generate a write per keystroke — and optimistic, so the page never lags behind the field.
   *
   * THE PENDING PATCH ACCUMULATES; IT DOES NOT REPLACE (BUGS.md 2026-08-29). This debounced the TIMER but not
   * the PAYLOAD: each call cleared the previous timeout and scheduled its own patch alone, so of any two edits
   * inside the window only the last one was ever sent. Naming a project and then writing its plot lost the
   * name — silently, because the optimistic `setProject` merge kept showing it until a reload.
   *
   * It hid for as long as it did because the fields that share a key survive: every `bible` patch is built
   * from the whole current bible, so a later one carries the earlier one's work by accident. Only edits to
   * DIFFERENT top-level keys — title vs bible vs genre — actually lost data, which is the rarer path by hand
   * and the guaranteed path for anyone filling the page out in order.
   */
  const projectSave = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingProject = useRef<{ title?: string; bible?: SeriesBible; genre?: string }>({});
  const patchProject = useCallback((patch: { title?: string; bible?: SeriesBible; genre?: string }) => {
    const id = series?.id;
    if (!id) return;
    setProject((p) => (p ? { ...p, ...patch } : p));
    // Shallow merge is the right depth: a `bible` patch is always the complete bible, so a newer one
    // legitimately supersedes an older one, while a `title` alongside it must survive rather than be dropped.
    pendingProject.current = { ...pendingProject.current, ...patch };
    if (projectSave.current) clearTimeout(projectSave.current);
    projectSave.current = setTimeout(() => {
      const merged = pendingProject.current;
      pendingProject.current = {};
      updateSeries(id, merged).catch(() => {
        // The edits did not land, so they stay pending and ride along with the next save rather than being
        // dropped on the floor — a failed write must not also lose what it was carrying.
        pendingProject.current = { ...merged, ...pendingProject.current };
        showError('Could not save your series', 'That change did not reach the server. Try again in a moment.');
      });
    }, 800);
  }, [series?.id, showError]);

  /**
   * SAVE NOW, and tell me when it landed.
   *
   * The project autosaves on an 800ms debounce, which is right for typing and wrong for the moment a creator
   * presses a button that depends on the server having their row. A reference plate is composed SERVER-side
   * from the project document (F2), so a plate requested a heartbeat after the description was typed asked
   * about a row the server had never seen — the walk caught it as a 404 and the creator would have seen
   * "could not make that reference" for a row plainly on their screen.
   *
   * So the flush is explicit: cancel the pending timer, send what is queued, and resolve when it is written.
   * A failed write rejects, so the caller can decline to spend rather than generating against stale facts.
   */
  const flushProject = useCallback(async () => {
    const id = series?.id;
    if (!id) return;
    if (projectSave.current) { clearTimeout(projectSave.current); projectSave.current = null; }
    const merged = pendingProject.current;
    if (!Object.keys(merged).length) return;
    pendingProject.current = {};
    try {
      await updateSeries(id, merged);
    } catch (e) {
      pendingProject.current = { ...merged, ...pendingProject.current };
      throw e;
    }
  }, [series?.id]);

  /**
   * REFERENTIALLY STABLE, both of them. The board syncs its node state from a `useMemo` that lists these as
   * dependencies, so a fresh array or a fresh closure on every render is an infinite loop — set nodes,
   * re-render, new identity, set nodes again. Cheap to get wrong and expensive to notice.
   */
  const boardLocations = useMemo(
    () => (project?.bible?.locations || []).map((l) => ({ id: l.id, name: l.name, image_path: l.image_path })),
    [project?.bible?.locations],
  );
  const boardObjects = useMemo(
    () => (project?.bible?.props || []).map((p2) => ({ id: p2.id, name: p2.name, image_path: p2.image_path })),
    [project?.bible?.props],
  );
  /**
   * THE CAST'S FACES, for the picker (F4). A cast row holds a POINTER (`character_id`) rather than a picture,
   * so the sheets are read once per project and joined here. Best-effort and quiet: without them the picker
   * still lists names exactly as it did, and the inspector strip still reports which faces will travel.
   */
  const [castFaces, setCastFaces] = useState<Record<string, string>>({});
  useEffect(() => {
    const sid = series?.id;
    if (!sid) { setCastFaces({}); return; }
    let alive = true;
    listSeriesCast(sid)
      .then((rows) => {
        if (!alive) return;
        const byId: Record<string, string> = {};
        for (const c of rows) if (c.id && c.reference_path) byId[c.id] = c.reference_path;
        setCastFaces(byId);
      })
      .catch(() => { /* names without faces is the old behaviour, not a failure worth surfacing */ });
    return () => { alive = false; };
  }, [series?.id]);

  const boardPeople = useMemo(
    () => (project?.bible?.cast || []).filter((c) => c.name).map((c) => ({
      id: c.id,
      name: c.name,
      image_path: c.character_id ? castFaces[c.character_id] || null : null,
    })),
    [project?.bible?.cast, castFaces],
  );
  /**
   * NAMING WHO, WHERE AND WHAT SAVES IMMEDIATELY — these are DECISIONS, not typing.
   *
   * The autosave debounce exists so a keystroke does not become a write; a pick from a modal is the opposite
   * of a keystroke, and it is also the moment the inspector asks the server which reference pictures this shot
   * will use. Left on the debounce, the strip asked about the shot as it was BEFORE the pick and then had no
   * reason to ask again, so a creator saw "nothing chosen yet" beside the thing they had just chosen. Flushing
   * here keeps the answer honest (found by the creation-flow review walk 2026-09-12).
   */
  const commitShot = useCallback((shotIndex: number, patch: Partial<StoryboardShot>) => {
    patchShot(shotIndex, patch);
    setPendingFlush(true);
  }, [patchShot]);
  const setShotPeople = useCallback(
    (shotIndex: number, characterIds: string[]) => commitShot(shotIndex, { character_ids: characterIds }),
    [commitShot],
  );
  const setShotObjects = useCallback(
    (shotIndex: number, propIds: string[]) => commitShot(shotIndex, { prop_ids: propIds }),
    [commitShot],
  );
  const setSceneLocation = useCallback(
    (openingShotIndex: number, locationId: string) => commitShot(openingShotIndex, { location_id: locationId }),
    [commitShot],
  );

  /**
   * Turn a planned row into a real episode. Creates the storyboard against this project, stamps the row so the
   * ladder can say `made / planned`, and navigates to it — a plan the creator opened should land them in the
   * writing, not back on the list they pressed from.
   */
  const openPlannedEpisode = useCallback(async (n: number) => {
    if (!series?.id) return;
    try {
      const made = await createStoryboard({ series_id: series.id, episode_number: n });
      patchProject({
        bible: {
          ...(project?.bible || {}),
          plan: (project?.bible?.plan || []).map((r) => (r.n === n ? { ...r, storyboard_id: made.id } : r)),
        },
      });
      // Opened IN PLACE rather than by navigating: the Studio already holds this project, and a route change
      // would tear the panel down and rebuild it to show a document we are holding.
      loadEpisode(made);
      goStage('story');
    } catch {
      showError('Could not start that episode', 'It was not created. Please try again.');
    }
  }, [series?.id, project?.bible, patchProject, loadEpisode, goStage, showError]);

  const statuses = stageStatuses({
    projectFilled: projectFilled(project?.bible),
    projectRequired: REQUIRED_BIBLE_FIELDS.length,
    idea,
    castCount: cast.length,
    directorHired,
    shotCount: shots.filter(isWrittenShot).length,
    lockedCount: shots.filter((sx) => sx.locked).length,
    hasFinalVideo: !!finalPath,
    published,
  });

  /* Reopening a production lands on the first UNFINISHED stage, never back at the start — until the creator
     navigates themselves, after which we leave them where they are. */
  useEffect(() => {
    if (stageChosen.current || !storyboardId) return;
    setStage(resumeStage(statuses));
    // Only on load of a production: `statuses` changes on every keystroke and must not re-drive navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resume is a load-time decision, not a live one
  }, [storyboardId]);

  /** Is a right pane mounted beside us to reserve room for? The host says so on the workstation (where the
   *  pane exists whether or not this production has a crew chat); otherwise it is the chat we launched from. */
  const hasChatRail = chatRailAvailable ?? !!conversationId;

  return (
    <StudioWorkspace
      open={open}
      title="Video Studio"
      /* THE JOURNEY, on the header line. This is how a creator knows where they are and what is left. Stages
         are never hard-gated: you can jump back to change a line after locking shots, but each stage knows
         whether it is done, so the rail guides without trapping. It rides in the header rather than above the
         page (owner 2026-08-23) — one row of chrome instead of three — which also keeps it in view when a
         long stage scrolls. Before there IS a production (the stream picker) there is no journey to show. */
      /* THE PROJECT SURFACE IS NOT A STEP in the episode's journey (owner 2026-08-24) — showing the episode
         stepper above it would say the plot and the world are per-episode decisions, which is the confusion
         the two levels exist to end. It names the project instead, and offers the way back to the episode. */
      toolbar={stage === 'project'
        ? (
          <div className="flex min-w-0 items-center gap-2">
            <span className={`min-w-0 truncate ${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>
              {project?.title || series?.title || 'Your series'}
            </span>
            <span className={`shrink-0 ${TYPE.meta} text-secondary-400`}>series</span>
            {storyboardId && (
              <button
                type="button"
                onClick={() => goStage('story')}
                className={`ml-2 shrink-0 rounded-lg px-2 py-1 ${TYPE.control} text-secondary-500 transition-colors
                            hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800`}
              >
                Back to episode {series?.episode ?? 1}
              </button>
            )}
          </div>
        )
        : (stream || storyboardId || series)
          ? (
            /* THE SERIES, NAMED BEFORE THE STEPS (navigation review, owner 2026-09-26). The series page was
               reachable only through a link inside Story, so a creator following the steps never saw the level
               everything inherits from. It is NOT made a step (the 2026-08-24 rule above still holds: that
               would read as a per-episode decision) — it is the breadcrumb the episode's steps sit under. */
            <div className="flex min-w-0 items-center gap-1">
              {onHome && (<>
                <button
                  type="button"
                  onClick={onHome}
                  data-testid="home-crumb"
                  className={`shrink-0 rounded-md px-1.5 py-0.5 ${TYPE.control} text-secondary-500 transition-colors
                              hover:bg-secondary-100 hover:text-secondary-900 dark:text-secondary-400
                              dark:hover:bg-secondary-800 dark:hover:text-white`}
                >
                  Your series
                </button>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-secondary-400" aria-hidden />
              </>)}
              {/* Only once there is a series to open: an unsaved first episode has no series page yet. */}
              {(storyboardId || series) && (<>
              <Tooltip content="Open the series: plot, world, cast and look">
                <button
                  type="button"
                  onClick={() => goStage('project')}
                  data-testid="series-crumb"
                  className={`min-w-0 max-w-[20rem] truncate rounded-md px-1.5 py-0.5 ${TYPE.control} text-secondary-500
                              transition-colors hover:bg-secondary-100 hover:text-secondary-900 dark:text-secondary-400
                              dark:hover:bg-secondary-800 dark:hover:text-white`}
                >
                  {project?.title || series?.title || 'Untitled series'}
                </button>
              </Tooltip>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-secondary-400" aria-hidden />
              </>)}
              <StageStepper current={stage} statuses={statuses} onGo={goStage} />
            </div>
          )
          : undefined}
      onClose={onClose}
      leftRail={leftRail}
      leftWidth={leftWidth}
      footer={footer}
      maximized
      /* A FOLDED pane is still a pane: it keeps its 3rem rail, so `chatRail` now says only whether a pane
         exists beside us — how much room it wants is `chatRailOpen`'s business. */
      chatRail={hasChatRail}
      chatRailOpen={chatRailOpen !== false}
      chatWidth={chatWidth}
      smooth={smooth}
    >
      {/* Generous BOTTOM padding: the footer bar used to hold the page open; without it the content ended
          flush against the viewport edge (owner 2026-08-16: "still keep some padding at bottom"). */}
      <div className={maximized ? 'p-4 pb-12 md:p-6 md:pb-16 md:mx-auto md:max-w-7xl' : 'p-4 pb-12'}>
        {/* NO CREATE / LIBRARY TABS (owner 2026-08-14): "Library will be on left pane that can slide in and
            out". The AssetExplorer in the left pane IS the library — a second tab holding the same content
            put the creator's work in two places and made the Studio argue with its own navigation. */}

        {/* SeriesLibrary lived here behind the Library tab. The left pane's AssetExplorer now lists series and
            their episodes, so this was the same content in a second place. */}
        {/* NO "WHAT ARE YOU MAKING?" GATE (RF3-NOSTREAM, owner 2026-08-23, screenshot95). A full-page picker
            stood between the creator and the Studio, asking them to classify a video before making one — and
            the pipeline does not branch on the answer: story → cast → crew → shots → publish is the same work
            whatever the video is for. The stage stepper already says where they are, so landing in the Studio
            now lands IN the journey. `StreamPicker` + `STREAMS` stay in the tree for the format defaults; the
            question is what is gone. */}
        <>
        {/* Desktop full-screen becomes a TWO-PANE workspace — a compact setup rail + the storyboard as the hero.
            ON THE SHOTS STAGE THERE IS NO RAIL: the canvas IS the page (owner directive 2026-08-13 — "not split
            page, just all on canvas"), so the board gets the full width instead of two thirds of it.
            Docked (narrow panel) + mobile keep the single-column stack (the wrappers carry no classes there). */}
        {/* Stages that are their OWN PAGE get the full width; only the stages still carrying setup controls
            keep the two-pane split. Story is a writing surface and Shots is the canvas — neither is a rail. */}
        {/* NO TWO-PANE SPLIT (RF3-DEVICE, 2026-08-15). Every stage's content is stage-scoped now, so the old
            setup-rail column had nothing left to be a rail OF — but Cast, Crew and Publish were still being
            squeezed into its 360px, which is what crushed the casting card to a one-word-per-line text column
            (screenshot72). Each stage is a full-width page on the workstation screen. */}
        <div>
        {/* Setup rail: sticky + independently scrollable on desktop full-screen, so the long storyboard scrolls past it.
            Hidden entirely on the shots stage — its controls belong to the earlier stages. */}
        <div className={stage === 'shots' ? 'hidden' : ''}>
        {/* The "Micro-drama · Change" chip went with the picker it led back to (RF3-NOSTREAM C): a label for a
            choice nobody is asked to make is chrome, and its Change link was the only door left to the gate. */}
        {/* The kind of video. HIDDEN once a work stream is chosen — the stream already answered this question and
            re-asking it was the panel's most-duplicated control. Still shown when resuming a storyboard or a series
            episode, where there is no stream and this is the only way to set the mode. */}
        {!stream && (
        <>
        <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-400 dark:text-secondary-500">
          What are you making? <InfoHint text="Micro-drama = a short story in shots (hook → cliffhanger), great for a series. Ad hook is a punchy 15–30s vertical ad, and is not open yet." />
        </div>
        {/*
          AD HOOK IS PARKED, NOT DELETED (owner 2026-09-13: *"remove Ad hook fragment for now... or just gray
          it out for now"*). Greyed rather than removed because the ad path is real in the product thesis and
          this is one line to reverse — a deleted button would have to be rediscovered and rebuilt.
          KNOWN CONSEQUENCE, stated so it is a decision rather than a drift: with one option selectable this is
          a picker that picks nothing, which the focused-journey law says should not exist. It earns its place
          only while the second option is arriving. If the ad path slips further, delete the whole block and
          set the mode to story rather than leaving a disabled pair on the page.
        */}
        <div className="mb-4 flex gap-2">
          {([['story', 'Micro-drama', 'A short story told in shots — hook to cliffhanger', false],
            ['hook', 'Ad hook', 'Not open yet — coming later', true]] as const).map(([m, label, hint, parked]) => (
            <button
              key={m}
              type="button"
              title={hint}
              disabled={parked}
              aria-disabled={parked}
              data-testid={`mode-${m}`}
              onClick={() => {
                if (mode === m || parked) return;
                setMode(m);  setError('');
              }}
              className={mode === m
                ? 'flex-1 rounded-xl border border-secondary-900 dark:border-white bg-secondary-100 dark:bg-secondary-800 px-3 py-2 text-xs font-semibold text-secondary-900 dark:text-white'
                : parked
                  ? 'flex-1 cursor-not-allowed rounded-xl border border-dashed border-secondary-200 px-3 py-2 text-xs text-secondary-400 dark:border-secondary-700 dark:text-secondary-600'
                  : 'flex-1 rounded-xl border border-secondary-200 dark:border-secondary-700 px-3 py-2 text-xs text-secondary-600 dark:text-secondary-300 hover:border-secondary-400'}
            >
              {label}{parked && <span className="ml-1 font-normal">· soon</span>}
            </button>
          ))}
        </div>
        </>
        )}
        {series && (
          <div className="mb-3 rounded-lg border border-secondary-200 dark:border-secondary-800 bg-secondary-50/60 dark:bg-secondary-800/30 px-3 py-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-secondary-500 dark:text-secondary-400">Episode {series.episode}{series.title ? ` of ${series.title}` : ' of this series'}</span>
              {/* "Make standalone" removed 2026-08-23 (owner). RF3-SCHEMA made EVERY production a series — a
                  single video is simply a series of one — so detaching a production from its series is no
                  longer a state the data model has. All the button did was clear local `series` state, which
                  then disagreed with the server until the next reload. */}
            </div>
            {series.continuesFrom && (
              <p className="mt-1 text-[11px] text-secondary-400 dark:text-secondary-500">Draft picks up last episode’s cliffhanger.</p>
            )}
          </div>
        )}
        {/* GENRE TEMPLATE CHIPS REMOVED (owner 2026-08-14, screenshot68): "not required, as it can be driven
            by prompt". A row of tropes is a weaker input than a sentence in the creator's own words, and it
            pushed the idea itself below the fold. The genre KB still shapes the draft — retrieved from what
            they WRITE rather than picked from a list. */}

        {/* STAGE 1 is a PAGE now, not a field in a rail. PRODUCT.md describes a screenwriter asking what the
            film is about — who, what, where, why — which is a writing surface; a one-line input invited six
            words and gave the drafter nothing to work with. The example still follows the chosen stream. */}
        {/* STORY IS ONE DOCUMENT, top to bottom (owner 2026-08-23): idea, then the spark, then the script it
            became. The side-by-side split made the script look like a separate artifact parked next to the
            idea, and it starved both columns of width inside a pane-flanked canvas. */}
        {/* PROJECT — the micro-drama's own facts, which every episode below inherits (RF3-LEVEL). */}
        {stage === 'project' && (
          <ProjectStage
            title={project?.title || series?.title || ''}
            onTitleChange={(v) => patchProject({ title: v })}
            bible={project?.bible || {}}
            onBibleChange={(bible) => patchProject({ bible })}
            onFlush={flushProject}
            episodeCount={project?.episode_count}
            loading={(projectLoading && !project) || !series?.id}
            seriesId={series?.id || null}
            onNeedsTopUp={() => setTopUp({ action: 'write that for you' })}
            /**
             * OPEN A PLANNED ROW AS A REAL EPISODE (RF3-SHOWPLAN C). This is the one place a plan becomes a
             * document — planning itself mints nothing. The row's number becomes the episode number, and the
             * row is stamped with the storyboard id so the ladder reads `made / planned` truthfully.
             */
            onOpenPlannedEpisode={(n) => { void openPlannedEpisode(n); }}
            templates={genres.map((g) => ({ key: g.key, label: g.label, premise: g.premise, category: g.category }))}
            genre={project?.genre || ''}
            onPickGenre={(key) => {
              // An empty key un-picks WITHOUT touching the plot the creator has since written.
              patchProject({ genre: key });
              if (!key) return;
              const g = genres.find((x) => x.key === key);
              // A plot seeds the premise only when there is nothing there to overwrite — at project level the
              // premise is the show itself, and silently replacing it would be destructive.
              if (g && !(project?.bible?.premise || '').trim()) patchProject({ bible: { ...(project?.bible || {}), premise: g.premise } });
            }}
            leads={leads}
            leadId={leadId}
            onPickLead={(id) => recruit(id).catch((e: Error) => {
              showError('Couldn’t bring them on board', e?.message || 'That lead could not join your crew. Please try again.');
            })}
          />
        )}
        {stage === 'story' && (
        <div className="mx-auto w-full max-w-2xl py-5">
        <StoryStage
          episodeTitle={episodeTitle}
          onEpisodeTitleChange={(v) => { setEpisodeTitle(v); setSavedAt(false); }}
          episodeNumber={series?.episode || 1}
          projectTitle={project?.title || series?.title || ''}
          idea={idea}
          onIdeaChange={(v) => { setIdea(v); setSavedAt(false); }}
          placeholder={stream?.ideaPrompt || (mode === 'hook' ? 'e.g. a reusable cup that keeps drinks hot 12 hours' : 'e.g. a cozy morning coffee ad')}
          onDraft={draftStory}
          drafting={drafting}
          draftLabel={series?.continuesFrom ? 'Develop the next episode' : 'Develop this into a script'}
          hint={series?.continuesFrom ? 'Picks up last episode’s cliffhanger.' : null}
          writers={writers}
          writerId={writerId}
          /* Recruiting is JOINING THE TEAM (owner 2026-08-16): the writer lands on this production's crew the
             moment they are picked, and the seat reads back from that crew — one truth, so a reopened
             production seats the writer who was actually hired. The promise is RETURNED so the seat can show
             it working; a failure is said out loud rather than leaving the creator believing they hired. */
          onPickWriter={(id) => recruit(id).catch((e: Error) => {
            showError('Couldn’t bring them on board', e?.message || 'That writer could not join your crew. Please try again.');
          })}
          seriesId={series?.id || null}
          onNeedsTopUp={() => setTopUp({ action: 'write that for you' })}
          setting={episodeSetting}
          onSettingChange={(v) => { setEpisodeSetting(v); setSavedAt(false); }}
          featuring={episodeFeaturing}
          onFeaturingChange={(v) => { setEpisodeFeaturing(v); setSavedAt(false); }}
          inheritedSetting={project?.bible?.setting}
          inheritedCast={project?.bible?.characters}
          inherited={{
            projectTitle: project?.title || series?.title || '',
            plotLabel: genres.find((g) => g.key === (project?.genre || ''))?.label,
            premise: project?.bible?.premise,
            era: project?.bible?.era,
            setting: project?.bible?.setting,
            characters: project?.bible?.characters,
            continuesFrom: series?.continuesFrom,
          }}
          onOpenProject={() => goStage('project')}
        />
        <ScriptPreview
          draft={scriptView}
          /* Committed work is shown as committed: no second Approve button for something already approved. */
          approved={!scriptDraft && !!approvedScript}
          drafting={drafting}
          onApprove={approveScript}
          onRegenerate={draftStory}
          onEditBeat={editBeat}
        />
        </div>
        )}
        {/* The duplicate "Draft the story into shots" button lived here, OUTSIDE the stage guard, so it drew on
            every page — and it was wrong anyway: Story does not draft shots. StoryStage owns the one action on
            that page, and the corrected flow (write -> pick a writer -> approve a script) is RF3-STORY.
            Hook variations went with it: those are for ad streams, and we sell micro-drama. */}

        {/* LENGTH REMOVED (owner 2026-08-14): "not needed, as user is doing micro-drama — how they have
            duration for shots is already enough". A total-length picker competed with the per-shot durations
            for the same decision, and the two could disagree. `duration_seconds` is still persisted, derived
            from the shots below, so the render and the library keep their number. */}

        {/* Video LOOK is a cost/quality decision, so it sits with the shots it prices — Generate is on the
            canvas now, and a quality choice made three pages away from the spend is a choice made blind. */}
        {stage === 'shots' && (
          <MediaAliasPicker aliases={aliases} value={videoAlias} onChange={(k) => { setVideoAlias(k); setSavedAt(false); }} />
        )}

        {/* SCENE FLOW REMOVED (owner 2026-08-14): "part of the director's style". How shots in one place
            connect is a directing choice, so it comes from the hired director rather than a chip the creator
            has to reason about. `continuity_mode` stays on the document at its 'auto' default. */}

        {/* MUSIC REMOVED (owner 2026-08-14): "part of a selected Agent's handling of the general story prompt,
            director's script, and so forth — so it's a hiring process, not user specifying the music". This is
            PRODUCT.md Stage 3: you hire a composer, you do not fill in a music form. The `sound` field stays on
            the document for the composer to write. */}

        {/* CREW page — hiring the craft leads. The director leads: picking their style is the creative decision
            that most changes the output, and a hired agent then OWNS its craft (which is why music and scene
            flow are no longer settings anywhere). */}
        {/* CAST + CREW ARE THE PROJECT'S (owner 2026-08-24). The same faces and the same director carry every
            episode — that consistency IS the series — so they sit on the project surface with the plot and
            the world, not as two more pages inside each episode. `/cast` and `/director` still resolve as
            addresses, so an old bookmark lands somewhere sensible rather than nowhere. */}
        {(stage === 'project' || stage === 'cast' || stage === 'director') && (
          <div className="mx-auto mt-6 w-full max-w-2xl space-y-6 border-t border-secondary-200 pt-6 dark:border-secondary-600">
            <div>
              <h3 className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>Your cast</h3>
              <p className={`mt-0.5 mb-2 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>
                The faces that stay the same in every episode.
              </p>
              <CastBible seriesId={series?.id || null} uid={uid} conversationId={conversationId || null} onCastChange={setCast} />
            </div>
            <div>
              <h3 className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>Your crew</h3>
              <p className={`mt-0.5 mb-2 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>
                Who directs and scores the whole thing.
              </p>
              <DirectorChair
                onRecruit={recruit}
                onDismiss={dismiss}
                memberProfileIds={crewIds}
                /* A hire changes the roster, and the roster is what the project reads — without this it would
                   not tick over until the whole panel happened to re-render. */
                onHired={() => setDirectorHired(true)}
              />
              <CrewStrip specialists={specialists} memberProfileIds={crewIds} onRecruit={recruit} onDismiss={dismiss} />
            </div>
          </div>
        )}
        </div>{/* end setup rail */}

        <div>
        {finalUrlFailed && (
          // The cut exists but could not be loaded here. Said out loud: a silent miss reads as "no film".
          <p role="status" data-testid="final-video-unavailable" className={`mb-4 ${TYPE.control} text-secondary-500 dark:text-secondary-400`}>
            Your video is rendered, but it could not be loaded here. Reload the page to watch it.
          </p>
        )}
        {finalUrl && (
          // The finished video — top of the storyboard pane, where it's the payoff of the work.
          <div className="mb-4" data-testid="final-video">
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-400 dark:text-secondary-500"><Check className="h-3.5 w-3.5" /> Your video</div>
            <CaptionedVideo src={finalUrl} captions={captions} className="md:max-w-md" />
          </div>
        )}
        {/* Comic-strip preview — the cheap propose→approve gate before the expensive render. Shots page only:
            it is about the frames you are reviewing. */}
        {/* THE FREE FIRST STORYBOARD (TRY-2/TRY-4): how much of it is left, then the step to bring it to life. */}
        {stage === 'shots' && canRender && (
          <div className="mb-3">
            <TrialNotice
              where="episode"
              storyboardId={storyboardId}
              hasKeyframes={shots.some((sx) => !!sx.previewFramePath)}
              refreshKey={saveTick}
            />
          </div>
        )}
        {stage === 'shots' && (
          <div className="mb-3 flex justify-end" role="tablist" aria-label="How to see the shots">
            <div className="inline-flex rounded-lg border border-secondary-200 p-0.5 dark:border-secondary-700">
              {(['script', 'board'] as const).map((v) => (
                <button key={v} type="button" role="tab" aria-selected={shotsView === v} data-testid={`shots-view-${v}`}
                  onClick={() => chooseShotsView(v)}
                  className={`rounded-md px-3 py-1 ${TYPE.control} font-semibold ${shotsView === v
                    ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900'
                    : 'text-secondary-500 hover:text-secondary-900 dark:text-secondary-400 dark:hover:text-white'}`}>
                  {v === 'script' ? 'Script' : 'Arrange'}
                </button>
              ))}
            </div>
          </div>
        )}
        {stage === 'shots' && shotsView === 'script' && (
          <ShotScriptView
            shots={shots}
            people={boardPeople}
            onEditLine={(i, line) => patchShot(i, { line })}
            onPreview={() => { void runPreview(false); }}
            previewing={previewing}
            onRedraw={(index) => { void runPreview(false, [index]); }}
            onQueueRender={canRender ? (q) => { void generate(q); } : undefined}
            queueing={busy}
            disabled={rendering}
          />
        )}
        {stage === 'shots' && shotsView === 'board' && canRender && (
          <PreviewStrip
            shots={shots}
            previewing={previewing}
            failedIndexes={previewFailed}
            onPreview={runPreview}
            onRedraw={(index) => { void runPreview(false, [index]); }}
            disabled={rendering}
          />
        )}
        {stage === 'shots' && shotsView === 'board' ? (
          // SHOTS page: the canvas + the selected shot's editor beneath it. Scoped to its own stage — it was
          // gated on `maximized`, which is now always true, so the board was drawing on every page.
          <div className="mt-5">
            {/* The board is a real canvas (React Flow): pan, zoom, a minimap, drag-to-reorder and keyboard
                reordering. A canvas has no intrinsic size, so it needs an explicit height — but a FIXED 520px
                was both too short for a full shot node (which is ~450 tall with its scene frame, so the row
                clipped) and a waste of a large screen. Scale with the viewport instead, with a floor that
                still fits one node and its frame. */}
            <div className="h-[clamp(520px,68vh,860px)] w-full overflow-hidden rounded-xl border border-secondary-200 dark:border-secondary-800">
              <StudioBoard
                shots={shots}
                selectedIndex={selectedShot}
                onSelect={setSelectedShot}
                onReorder={reorderShots}
                onAddShot={shots.length < MAX_SHOTS ? addShot : undefined}
                atShotCap={shots.length >= MAX_SHOTS}
                onShotChange={patchShot}
                /* THE PROJECT'S PLACES, on the board (RF3-WORKFLOW §4). A scene band names one, and every shot
                   in it then reuses that location's wording — and its anchor object — byte-identically at
                   render time. This is where the project level finally reaches the canvas. */
                storyboardId={storyboardId}
                savedTick={saveTick}
                savedShotCount={savedShotCount}
                locations={boardLocations}
                onSetSceneLocation={setSceneLocation}
                objects={boardObjects}
                onSetShotObjects={setShotObjects}
                people={boardPeople}
                onSetShotPeople={setShotPeople}
                /* The MUSIC LANE is deliberately not on the board (owner call 2026-08-13). The left panel already
                   carries the music choice for the common case, per-span beds are a late audio refinement rather
                   than a shot-board concern, and the composer works ACROSS the shots — so the lane cost the canvas
                   permanent height to duplicate a decision made elsewhere. `music_spans` still persists and
                   MusicLane still exists for a future audio step. */
                onRemakeShot={storyboardId ? remakeOneShot : undefined}
                remakingIndex={remakingIndex}
                onAssistShot={(i) => { void assistShot(i); }}
                assistingIndex={assistingIndex}
                onDuplicateShot={shots.length < MAX_SHOTS ? duplicateShot : undefined}
                onDeleteShot={shots.length > 1 ? removeShot : undefined}
                onRestoreTake={storyboardId ? restoreOneTake : undefined}
                /* W6: the board was built with per-node execution states and a queue panel, but nothing ever
                   fed them — during a 20-40 minute render the only signal was one line of footer text. The
                   render sweep completes shots in order, so the count of finished clips IS the index of the
                   one currently being worked on. */
                renderingIndex={rendering ? Math.min(renderedCount, shots.length - 1) : null}
                onQueueRender={canRender ? generate : undefined}
                queueing={busy}
              />
            </div>
            {/* NO SHOT INSPECTOR CARD (owner 2026-08-14). The shot's line is edited ON its node as a widget,
                so a "Shot 1" card repeating the same field below the canvas was the same value editable in two
                places — congestion by our own rule. Per-shot actions live on the node and its selection
                toolbar (assist, re-make, approve, duplicate, delete). */}
          </div>
        ) : null /* Shots live ONLY on the Shots page. This branch used to be the docked list, and when the
                    condition changed from `maximized` to the stage it started dumping every shot card onto
                    Story, Cast, Crew and Publish — which is what "remove the Shot 1 card across these pages"
                    was seeing. There is no docked mode any more, so there is nothing to fall back to. */}

        {/* PUBLISH before there is anything to publish. This used to be a blank canvas: the publish block needs a
            saved episode, and with none the page drew nothing at all — no reason, no way on. */}
        {stage === 'publish' && !storyboardId && (
          <div className="mt-10 flex flex-col items-center gap-2 text-center">
            <p className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>Nothing to publish yet</p>
            <p className={`max-w-sm ${TYPE.body} text-secondary-500 dark:text-secondary-400`}>
              Write this episode on Story and shoot it on Shots. Once it is cut, this is where it goes on your store.
            </p>
            <button
              type="button"
              onClick={() => goStage('story')}
              className={`mt-1 rounded-md bg-secondary-900 px-3 py-1.5 ${TYPE.control} font-medium text-white
                          dark:bg-white dark:text-black`}
            >
              Start on Story
            </button>
          </div>
        )}

        {/* PUBLISH page — the business side (RF3 spec: pricing, where it belongs, ownership paperwork). */}
        {stage === 'publish' && storyboardId && (
          <div className="mt-6 border-t border-secondary-200 dark:border-secondary-800 pt-4">
            {/* THE WHOLE POINT OF FINISHING (RF3-STORE). The publish route shipped without a control, so a
                store could only be filled by hand — this is the door. */}
            <PublishToStore
              storyboardId={storyboardId}
              published={published}
              hasFilm={!!finalPath}
              hasDrafts={shots.some((s2) => s2.draft && s2.clipPath)}
              storeShortId={storeShortId}
              onChange={setPublished}
            />
            <div className="mt-4 border-t border-secondary-200 pt-4 dark:border-secondary-600">
              <CopyrightPanel storyboardId={storyboardId} memberProfileIds={crewIds} onRecruit={recruit} />
            </div>
          </div>
        )}

        {error && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-secondary-500 dark:text-secondary-400" role="status">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
          </p>
        )}

        {/* W13 — format-fit advisories. Amber (a caution, not a failure): the creator may be writing a finale
            deliberately, so this never blocks Generate. Shown before render spend so a re-draft is free. */}
        {/* The advisories are about the DRAFT, so they live on the page that produced it — they used to draw
            outside every stage guard and followed the creator onto Cast, Crew and Publish. */}
        {stage === 'story' && formatNotes.length > 0 && (
          <div className="mt-3 space-y-1" role="status">
            {formatNotes.map((n) => (
              <p key={n.code} className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{n.message}
              </p>
            ))}
          </div>
        )}
        </div>{/* end storyboard pane */}
        </div>{/* end two-pane wrapper */}
        </>
      </div>
      <TopUpModal open={!!topUp} action={topUp?.action} onClose={() => setTopUp(null)} />
    </StudioWorkspace>
  );
};

export default StudioPanel;
